import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
/*
 * hls.js is TYPE-ONLY here and LOADED ON DEMAND below.
 *
 * It is 1.3 MB — a quarter of the app's main chunk — and it is needed only when
 * someone plays back a recording. Bundled statically, every person who opens the
 * app paid for a video decoder to sit unused, including the ones who never touch
 * a camera. The dynamic import moves it to its own chunk, fetched at the moment a
 * player attaches.
 *
 * The class is also the namespace for `Events` and `ErrorTypes`, and those are
 * read from handlers that run long after the attach — so the loaded module is
 * kept on the instance rather than reached for again.
 */
import type HlsType from 'hls.js';
import { t, getLocale, LocaleController } from '@vitesse/base-ui/i18n';
import { iconButton } from '@vitesse/base-ui/components/base-buttons';
import { statusBadge } from '@vitesse/base-ui/components/templates';
import type { CameraManager } from '../../managers/camera-manager';
import type { Camera } from '../../../core/domain/camera';
import type { CameraStreamManager } from './camera-stream-manager';
import './camera-feed';
import '@vitesse/base-ui/elements/vitesse-icon';
import { fmtTime } from '@vitesse/base-ui/format';

// Span end-labels. Circular (rolling) buffers read naturally as "how long ago"
// (24 hr ago … 10 sec ago); a stop-when-full buffer is a fixed historical span, so
// we show the absolute wall-clock the recording was ON. Both i18n via Intl.
function fmtAgo(ms: number, locale: string): string {
  if (!(ms > 0)) return '';
  const sec = Math.max(0, Math.round((Date.now() - ms) / 1000));
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'always', style: 'short' });
  if (sec < 60) return rtf.format(-sec, 'second');
  if (sec < 3600) return rtf.format(-Math.round(sec / 60), 'minute');
  if (sec < 86_400) return rtf.format(-Math.round(sec / 3600), 'hour');
  return rtf.format(-Math.round(sec / 86_400), 'day');
}
function fmtAbs(ms: number, locale: string): string {
  if (!(ms > 0)) return '';
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(ms);
}
// One hls.js media fragment, structurally typed (we read only these three fields,
// so we don't couple to hls.js's full Fragment export). `programDateTime` is the
// segment's absolute wall-clock (ms epoch) parsed from #EXT-X-PROGRAM-DATE-TIME.
interface HlsFrag {
  start: number; // media-time start (seconds)
  duration: number; // seconds
  programDateTime: number | null; // absolute wall-clock ms (null if the tag is absent)
}

// Open playback this far back from the live edge: shows current footage immediately
// (recent segments load first — fast, esp. a cloud leaf-pull of fresh data) with a
// little run-up to play forward; older footage lazy-loads as the user scrubs left.
const START_NEAR_EDGE_S = 10;
// Releasing the scrub handle within this fraction of the right edge snaps to LIVE (the
// WebRTC feed) instead of seeking the HLS archive — the YouTube/Twitch "back to live".
const LIVE_EDGE_FRAC = 0.992;

/*
 * <vitesse-camera-playback> — the camera DVR. Sports-DVR model: the LIVE edge is the
 * real-time WebRTC feed (<vitesse-camera-feed>, ~1s, smooth — the same path the Live
 * tab uses), and the recorded PAST is HLS-VOD scrub-back. One timeline spans the whole
 * retained window; the handle pinned to the right edge = LIVE (WebRTC), dragged left =
 * recorded (hls.js seeks the archive). hls.js gives a single seek bar over the recording
 * via a CMAF-HLS VOD playlist with per-fragment PROGRAM-DATE-TIME (true wall-clock) +
 * DISCONTINUITY on gaps; the session Bearer rides each request through xhrSetup. HLS is
 * the wrong transport for "live" (chunked, edge-jitter) — WebRTC owns the edge; HLS owns
 * the past. Both video surfaces stay mounted (the feed hidden while reviewing) so the
 * live↔recorded switch is instant.
 *
 * Scrub preview (Apple-TV style): we replace the native <video controls> with our
 * OWN transport bar, because the native scrubber gives no live-drag hook and a
 * preview overlay would cover it. While the user drags the seek track, we show the
 * frame AT the scrub position by pulling a single keyframe from the camera's
 * snapshot endpoint (cmd.<gw>.cameras.<cid>.snapshot at the scrubbed wall-clock)
 * and decoding it on the VIEWER's hardware — the source does one cheap keyframe
 * read, never a heavy seek-decode, and requests are coalesced to one-in-flight so a
 * fast drag can't flood it. On release we seek hls.js to that point. The big win is
 * on LAN where the keyframe round-trip is a few ms → frames track the drag live.
 */

const STYLE_ID = 'vitesse-camera-playback-styles';
function ensureStyles(): void {
  // The player is a bordered card: a dark VIDEO STAGE on top (video always reads on
  // dark) + a THEMED control strip below (app surfaces/tokens, light & dark — NO black
  // overlay bar). The strip is in-flow (grid row), so the controls integrate with the
  // app instead of floating on the footage. Loading/error overlays sit on the dark
  // stage (light-on-dark). The time bubble + scrub preview float above the seek handle.
  const css = `
vitesse-camera-playback { display: block; height: 100%; }
.cam-pb { display: grid; grid-template-rows: minmax(0, 1fr) auto; width: 100%; height: 100%; min-height: 68vh;
  border: 1px solid var(--border-color); border-radius: var(--radius-md, 8px); overflow: hidden; background: var(--bg-secondary); }
.cam-pb.empty { display: flex; align-items: center; justify-content: center; min-height: 40vh; }
/* Video STAGE — dark letterbox; the WebRTC live feed + HLS archive video stack here. */
.cam-pb-stage { position: relative; min-height: 0; background: var(--media-backdrop); overflow: hidden; display: flex; align-items: center; justify-content: center; }
.cam-pb-stage video { width: 100%; height: 100%; display: block; object-fit: contain; background: var(--media-backdrop); }
.cam-pb-video, .cam-pb-live-feed { position: absolute; inset: 0; }
.cam-pb-video[hidden], .cam-pb-live-feed[hidden] { display: none; }
.cam-pb-overlay { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center;
  justify-content: center; gap: 8px; color: color-mix(in srgb, var(--overlay-color) 82%, transparent); font-size: 13px; pointer-events: none; }
.cam-pb.empty .cam-pb-overlay { position: static; color: var(--text-secondary); }
.cam-pb-ring { width: 26px; height: 26px; border-radius: 50%; border: 2px solid color-mix(in srgb, var(--overlay-color) 25%, transparent);
  border-top-color: var(--blue); animation: cam-pb-spin 0.9s linear infinite; }
@keyframes cam-pb-spin { to { transform: rotate(360deg); } }
.cam-pb-bridge { position: absolute; inset: 0; margin: auto; max-width: 100%; max-height: 100%; width: auto;
  height: auto; object-fit: contain; pointer-events: none; z-index: 1; }
/* THEMED control strip below the video — app surfaces, always visible (a control bar,
   not an auto-hiding overlay → more intuitive). */
.cam-pb-strip { display: flex; align-items: center; gap: 12px; padding: 11px 14px; background: var(--bg-secondary); border-top: 1px solid var(--border-color); }
.cam-pb-edge { font-size: 11px; font-variant-numeric: tabular-nums; color: var(--text-secondary); flex: none; white-space: nowrap; }
/* Seek track — themed; a wide invisible hit-area over the thin rail. */
.cam-pb-track { position: relative; flex: 1; height: 18px; display: flex; align-items: center; cursor: pointer; touch-action: none; }
.cam-pb-rail { position: absolute; left: 0; right: 0; height: 5px; border-radius: 3px; background: var(--bg-tertiary); overflow: hidden; }
.cam-pb-buf { position: absolute; left: 0; top: 0; bottom: 0; background: var(--border-color); border-radius: 3px; }
.cam-pb-played { position: absolute; left: 0; top: 0; bottom: 0; background: var(--blue); border-radius: 3px; }
.cam-pb-handle { position: absolute; top: 50%; width: 14px; height: 14px; border-radius: 50%; background: var(--blue);
  transform: translate(-50%, -50%); box-shadow: 0 0 0 3px var(--bg-secondary), 0 1px 3px color-mix(in srgb, var(--shadow-color) 35%, transparent); transition: transform 0.1s ease; }
.cam-pb-track:hover .cam-pb-handle, .cam-pb.scrubbing .cam-pb-handle { transform: translate(-50%, -50%) scale(1.18); }
/* Current-time bubble — floats DIRECTLY above the handle (tight clamp so it tracks the
   tip even at the edges), themed + readable in both modes. */
.cam-pb-bubble { position: absolute; bottom: calc(100% + 7px); transform: translateX(-50%); pointer-events: none; z-index: 4;
  background: var(--bg-tertiary); color: var(--text-primary); border: 1px solid var(--border-color); box-shadow: 0 2px 8px color-mix(in srgb, var(--shadow-color) 18%, transparent);
  padding: 3px 8px; border-radius: 6px; font-size: 11px; font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
/* Scrub keyframe preview — sits ABOVE the time bubble (larger clamp for its width). */
.cam-pb-preview { position: absolute; bottom: calc(100% + 36px); transform: translateX(-50%); width: 160px; aspect-ratio: 16 / 9;
  object-fit: cover; border-radius: 6px; border: 1px solid var(--border-color); box-shadow: 0 3px 12px color-mix(in srgb, var(--shadow-color) 28%, transparent); pointer-events: none; z-index: 3; background: var(--media-backdrop); }
/* LIVE — the app's status badge (red = at the live edge; gray + clickable = reviewing). */
.cam-pb-live-badge { background: transparent; border: none; padding: 0; flex: none; display: inline-flex; cursor: pointer; }
.cam-pb-live-badge[data-live] { cursor: default; }`;
  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  if (el.textContent !== css) el.textContent = css;
}

@customElement('vitesse-camera-playback')
export class CameraPlayback extends LitElement {
  @property({ attribute: false }) cameraManager?: CameraManager;
  /** The camera descriptor — used for `recordingCircular` (label style) + footage
   *  presence. The timeline's wall-clock comes from the loaded media's fragment PDT,
   *  NOT this descriptor's live (drifting) [availableFromMs, availableToMs] window. */
  @property({ attribute: false }) camera?: Camera;
  @property() cameraId = '';
  /** Whether the camera has any recorded footage (the descriptor's available window). */
  @property({ type: Boolean }) hasFootage = false;
  /** The shared WebRTC stream manager + gateway — the LIVE edge of the timeline is the
   *  real-time WebRTC feed (the same rock-solid path the Live tab uses); HLS serves only
   *  the recorded past (the sports-DVR model: WebRTC live + HLS scrub-back). */
  @property({ attribute: false }) cameraStreams?: CameraStreamManager;
  @property() gatewayId = '';

  @state() private status: 'none' | 'loading' | 'ready' | 'error' = 'none';
  @state() private live = true; // at the live edge → WebRTC feed; scrubbed back → HLS archive
  @state() private playing = false;
  @state() private playedFrac = 0; // playhead position over the seekable span
  @state() private bufferedFrac = 0;
  @state() private scrubbing = false;
  @state() private scrubFrac = 0; // handle position while dragging
  @state() private previewUrl?: string; // decoded keyframe at the scrub position
  @state() private headMs = 0; // wall-clock at the playhead (or scrub) — the time label
  @state() private bridgeUrl?: string; // latest keyframe shown while hls.js spins up (no black)
  @state() private videoReady = false; // the <video> has painted a frame → drop the bridge
  @state() private nowTick = 0; // bumped on a slow timer so the "X ago" end-labels stay fresh

  private hls?: HlsType;

  /** The loaded module, kept because `Events` and `ErrorTypes` are read from
   *  handlers that run long after the import resolved. */
  private HlsCtor?: typeof HlsType;
  private token: string | null = null;
  private tokenTimer = 0;
  private tickTimer = 0;
  private startedAtEdge = false; // seek-to-edge runs once, when the VOD duration is known
  private bridgeGen = 0; // invalidate a stale bridge fetch across a teardown / target swap
  private recoverAttempts = 0; // bounded auto-recovery before surfacing a fatal error
  // Scrub-preview pump: coalesce to one in-flight snapshot, always chasing the latest
  // fraction, so a fast drag issues at most one keyframe read per round-trip.
  private previewInFlight = false;
  private previewPendingFrac: number | null = null;
  private previewGen = 0; // invalidate stale decodes after a scrub ends / teardown

  constructor() {
    super();
    new LocaleController(this); // relative end-labels re-format on language change
  }

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  /** The HLS scrub-back <video>, scoped: <vitesse-camera-feed> renders its own
   *  <video> into our light DOM, so a bare querySelector('video') would be ambiguous. */
  private hlsVideo(): HTMLVideoElement | null {
    return this.querySelector<HTMLVideoElement>('video.cam-pb-video');
  }

  connectedCallback(): void {
    super.connectedCallback();
    ensureStyles();
    // Keep the relative "X ago" end-labels current between roster bumps (a paused
    // recording's window is static, so nothing else would re-render them). Cheap —
    // two text nodes, every 5s, only while the Recording tab is mounted.
    this.tickTimer = window.setInterval(() => {
      this.nowTick++;
    }, 5_000);
    void this.start();
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.tickTimer) {
      clearInterval(this.tickTimer);
      this.tickTimer = 0;
    }
    this.teardown();
  }
  protected willUpdate(changed: PropertyValues): void {
    // Restart the media pipeline only on identity/footage change — NOT on `camera`
    // deltas (the roster bumps it constantly as bytesUsed/availableToMs grow; the
    // transport reads the live window each frame instead).
    if (changed.has('cameraId') || changed.has('cameraManager') || changed.has('hasFootage')) {
      this.teardown();
      void this.start();
    }
  }

  private teardown(): void {
    const v = this.hlsVideo();
    if (v) {
      v.removeEventListener('play', this.onPlayState);
      v.removeEventListener('pause', this.onPlayState);
      v.removeEventListener('timeupdate', this.onTimeUpdate);
      v.removeEventListener('progress', this.onProgress);
      v.removeEventListener('loadeddata', this.onLoadedData);
      v.removeEventListener('durationchange', this.onDurationChange);
    }
    this.hls?.destroy();
    this.hls = undefined;
    this.recoverAttempts = 0;
    this.previewGen++;
    this.previewInFlight = false;
    this.previewPendingFrac = null;
    this.scrubbing = false;
    this.clearPreview();
    this.bridgeGen++;
    this.videoReady = false;
    this.startedAtEdge = false;
    this.live = true; // a fresh camera opens at the live edge (WebRTC), not a prior scrub
    this.clearBridge();
    if (this.tokenTimer) {
      clearInterval(this.tokenTimer);
      this.tokenTimer = 0;
    }
  }

  private async start(): Promise<void> {
    const m = this.cameraManager;
    if (!m || !this.cameraId || !this.hasFootage) {
      this.status = 'none';
      return;
    }
    const gw = m.gateway; // for the X-Vitesse-Gateway-Id header (cloud recording scope)
    this.status = 'loading';
    void this.fetchBridge(); // paint the latest keyframe immediately — no black while hls spins up
    try {
      const url = await m.hlsPlaylistUrl(this.cameraId);
      this.token = await m.getToken(); // prime (xhrSetup is synchronous)
      // Refresh well inside a session-token's lifetime so a scrub never fetches a
      // segment with a stale bearer (the prime + this keep xhrSetup's token fresh).
      this.tokenTimer = window.setInterval(async () => {
        this.token = (await m.getToken()) ?? this.token;
      }, 30_000);
      await this.updateComplete; // <video> in the DOM
      const video = this.hlsVideo();
      if (!video) return;
      video.addEventListener('play', this.onPlayState);
      video.addEventListener('pause', this.onPlayState);
      video.addEventListener('timeupdate', this.onTimeUpdate);
      video.addEventListener('progress', this.onProgress);
      video.addEventListener('loadeddata', this.onLoadedData);
      video.addEventListener('durationchange', this.onDurationChange);
      // Fetched here, at the one moment it is certainly needed. Awaited inside the
      // method that already awaits `updateComplete`, so nothing about the attach
      // order changes.
      const Hls = (this.HlsCtor ??= (await import('hls.js')).default);
      if (Hls.isSupported()) {
        const hls = new Hls({
          // Keep a generous back-buffer for instant backward scrubbing, but cap the
          // forward buffer so a seek loads only what's needed (snappier scrubbing).
          backBufferLength: 90,
          maxBufferLength: 24,
          maxMaxBufferLength: 60,
          // Tolerate transient segment/playlist load blips (LAN hiccups, a token
          // refresh racing a fetch) with several retries before hls.js calls it fatal.
          fragLoadPolicy: {
            default: {
              maxTimeToFirstByteMs: 10_000,
              maxLoadTimeMs: 30_000,
              timeoutRetry: { maxNumRetry: 4, retryDelayMs: 400, maxRetryDelayMs: 4000 },
              errorRetry: { maxNumRetry: 6, retryDelayMs: 400, maxRetryDelayMs: 4000 },
            },
          },
          xhrSetup: (xhr) => {
            if (this.token) xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
            // Tell the CLOUD api-gateway which gateway to pull recordings from — the
            // Connect transport conveys this header too; hls.js must as well, else the
            // cloud falls back to the org's first gateway. The edge ignores it (its own
            // single gateway). Without it, remote DVR playback hits the wrong gateway.
            xhr.setRequestHeader('X-Vitesse-Gateway-Id', gw);
          },
        });
        this.hls = hls;
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          this.recoverAttempts = 0;
          this.status = 'ready';
          // NOT play-from-0 (the oldest footage, ~24h ago, a slow cloud-pull of stale
          // segments). Jump to the live edge so recent segments load first; if the VOD
          // duration isn't known yet, durationchange runs this again once it is.
          this.maybeStartAtEdge(video);
        });
        hls.on(Hls.Events.FRAG_LOADED, () => {
          this.recoverAttempts = 0; // a good load clears the recovery budget
        });
        hls.on(Hls.Events.ERROR, (_evt, data) => {
          if (!data.fatal) return; // non-fatal: hls.js already retries per the policy above
          this.handleFatal(data.type);
        });
        hls.loadSource(url);
        hls.attachMedia(video);
      } else {
        this.status = 'error'; // no MSE (very old browser); native-HLS can't carry the Bearer
      }
    } catch {
      this.status = 'error';
    }
  }

  // Bounded auto-recovery: a fatal NETWORK error refreshes the bearer (covers a
  // token that expired mid-stream) and restarts loading; a fatal MEDIA error asks
  // hls.js to recover the decode pipeline. Only after the recovery budget is spent
  // do we surface "could not load". Prevents one transient blip from killing playback.
  private handleFatal(type: string): void {
    const hls = this.hls;
    if (!hls || this.recoverAttempts >= 3) {
      this.status = 'error';
      return;
    }
    this.recoverAttempts++;
    // Present by construction: this handler is only ever registered after the
    // import resolved. Guarded anyway rather than asserted, because the cost of
    // being wrong is a crash inside error recovery.
    const Hls = this.HlsCtor;
    if (!Hls) {
      this.status = 'error';
      return;
    }
    if (type === Hls.ErrorTypes.NETWORK_ERROR) {
      void (async () => {
        this.token = (await this.cameraManager?.getToken()) ?? this.token;
        hls.startLoad();
      })();
    } else if (type === Hls.ErrorTypes.MEDIA_ERROR) {
      hls.recoverMediaError();
    } else {
      this.status = 'error';
    }
  }

  // ── Transport: playhead tracking ───────────────────────────────────────────
  private readonly onPlayState = (): void => {
    const v = this.hlsVideo();
    this.playing = !!v && !v.paused && !v.ended;
  };
  private readonly onTimeUpdate = (): void => {
    if (this.scrubbing) return; // the handle follows the drag, not playback, mid-scrub
    const v = this.hlsVideo();
    if (!v) return;
    const [s, e] = this.seekableSpan(v);
    if (e > s) {
      this.playedFrac = Math.min(1, Math.max(0, (v.currentTime - s) / (e - s)));
      this.headMs = this.wallClockForMedia(v.currentTime); // true clock of the played frame
    }
  };
  // First decoded frame → the <video> covers the bridge still (drop it). Also a safety
  // net for the seek-to-edge in case durationchange landed before listeners attached.
  private readonly onLoadedData = (): void => {
    this.videoReady = true;
    const v = this.hlsVideo();
    if (v) this.maybeStartAtEdge(v);
  };
  // The VOD duration becoming known is the reliable moment to seek to the live edge.
  private readonly onDurationChange = (): void => {
    const v = this.hlsVideo();
    if (v) this.maybeStartAtEdge(v);
  };
  // Park the HLS archive near its recent edge so a scrub-back into recent footage is
  // instant (the buffer is already warm there), but PLAY it only when REVIEWING — at the
  // live edge the WebRTC feed is on screen and HLS stays paused underneath. Runs once.
  private maybeStartAtEdge(v: HTMLVideoElement): void {
    if (this.startedAtEdge || !isFinite(v.duration) || v.duration <= 0) return;
    this.startedAtEdge = true;
    v.currentTime = Math.max(0, v.duration - START_NEAR_EDGE_S);
    if (!this.live) void v.play().catch(() => undefined);
  }
  // ── DVR mode: LIVE (WebRTC edge) ↔ RECORDED (HLS archive) ────────────────────
  /** Snap to the live edge: the real-time WebRTC feed takes over; HLS pauses (warm). */
  private goLive(): void {
    if (this.live) return;
    this.live = true;
    this.hlsVideo()?.pause();
  }
  /** Leave the live edge: show the HLS archive and play from `mediaTime` (seconds). */
  private goRecorded(mediaTime: number): void {
    this.live = false;
    const v = this.hlsVideo();
    if (!v) return;
    const [s, e] = this.seekableSpan(v);
    v.currentTime = Math.min(e, Math.max(s, mediaTime));
    void v.play().catch(() => undefined);
  }
  private readonly onProgress = (): void => {
    const v = this.hlsVideo();
    if (!v || v.buffered.length === 0) return;
    const [s, e] = this.seekableSpan(v);
    if (e > s) this.bufferedFrac = Math.min(1, Math.max(0, (v.buffered.end(v.buffered.length - 1) - s) / (e - s)));
  };

  private seekableSpan(v: HTMLVideoElement): [number, number] {
    const sk = v.seekable;
    if (sk && sk.length > 0) return [sk.start(0), sk.end(sk.length - 1)];
    return [0, isFinite(v.duration) ? v.duration : 0];
  }
  // ── Wall-clock from the LOADED MEDIA (hls.js fragment PROGRAM-DATE-TIME) ──────
  // The timeline's clock comes from the segments actually loaded, NOT the live
  // roster window. The roster's [availableFrom,availableTo] grows under the user
  // (every bump), so mapping the scrubber through it drifted out from under the
  // frozen VOD — the "jumpy" axis + a right edge that promised footage the media
  // never had. Each fragment carries #EXT-X-PROGRAM-DATE-TIME (its true UnixNano
  // start), so media-time ↔ wall-clock is an exact per-fragment map that also
  // survives gaps (a discontinuity is just a PDT jump). This is the faithful,
  // non-drifting source the labels + snapshot use.
  private fragments(): HlsFrag[] {
    const h = this.hls;
    if (!h?.levels?.length) return [];
    const lvl = (h.levels[h.currentLevel] ?? h.levels[0]) as { details?: { fragments?: HlsFrag[] } } | undefined;
    return lvl?.details?.fragments ?? [];
  }
  /** Absolute wall-clock ms at a media-time position `t` (seconds), via fragment PDT. */
  private wallClockForMedia(t: number): number {
    const frags = this.fragments();
    if (frags.length === 0) return 0;
    for (const f of frags) {
      if (f.programDateTime == null) continue;
      if (t >= f.start - 0.05 && t < f.start + f.duration + 0.05) return f.programDateTime + (t - f.start) * 1000;
    }
    // Outside the fragment span (loading head / past the tail): clamp to the ends.
    const first = frags[0];
    const last = frags[frags.length - 1];
    if (first.programDateTime != null && t <= first.start) return first.programDateTime;
    if (last.programDateTime != null) return last.programDateTime + Math.max(0, t - last.start) * 1000;
    return 0;
  }
  /** The loaded recording's [oldest, newest] wall-clock bounds (the scrubber's ends). */
  private mediaSpanWallClock(): [number, number] {
    const frags = this.fragments();
    if (frags.length === 0) return [0, 0];
    const first = frags[0];
    const last = frags[frags.length - 1];
    return [first.programDateTime ?? 0, (last.programDateTime ?? 0) + last.duration * 1000];
  }
  /** Wall-clock ms at a scrub fraction of the seekable media span. */
  private wallClockForFrac(frac: number): number {
    const v = this.hlsVideo();
    if (!v) return 0;
    const [s, e] = this.seekableSpan(v);
    return this.wallClockForMedia(s + frac * (e - s));
  }

  // ── Transport: controls ─────────────────────────────────────────────────────
  private togglePlay(): void {
    const v = this.hlsVideo();
    if (!v) return;
    if (v.paused) void v.play().catch(() => undefined);
    else v.pause();
  }
  private toggleFullscreen(): void {
    const stage = this.querySelector('.cam-pb') as HTMLElement | null;
    if (!stage) return;
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    else void stage.requestFullscreen?.().catch(() => undefined);
  }

  // ── Transport: scrubbing + live preview ─────────────────────────────────────
  private fracFromPointer(ev: PointerEvent, track: HTMLElement): number {
    const r = track.getBoundingClientRect();
    return r.width > 0 ? Math.min(1, Math.max(0, (ev.clientX - r.left) / r.width)) : 0;
  }
  private readonly onScrubDown = (ev: PointerEvent): void => {
    const track = ev.currentTarget as HTMLElement;
    track.setPointerCapture(ev.pointerId);
    this.scrubbing = true;
    this.previewGen++; // a fresh scrub session
    this.applyScrub(this.fracFromPointer(ev, track));
    const move = (e: PointerEvent): void => this.applyScrub(this.fracFromPointer(e, track));
    const up = (e: PointerEvent): void => {
      track.releasePointerCapture(ev.pointerId);
      track.removeEventListener('pointermove', move);
      track.removeEventListener('pointerup', up);
      track.removeEventListener('pointercancel', up);
      this.endScrub(this.fracFromPointer(e, track));
    };
    track.addEventListener('pointermove', move);
    track.addEventListener('pointerup', up);
    track.addEventListener('pointercancel', up);
  };
  private applyScrub(frac: number): void {
    this.scrubFrac = frac;
    if (this.status !== 'ready') return;
    const v = this.hlsVideo();
    if (!v) return;
    const [s, e] = this.seekableSpan(v);
    const target = s + frac * (e - s);
    this.headMs = this.wallClockForMedia(target); // PDT-correct clock at the scrub point
    // When REVIEWING (HLS video visible) and the target is BUFFERED, seek it live — it
    // shows the REAL frame instantly (the Apple-TV "video scrubs under your finger" feel).
    // At the live edge (WebRTC on top, HLS hidden) or scrubbing into the not-yet-buffered
    // range, fall back to the keyframe-snapshot preview overlay instead.
    if (!this.live && e > s && this.isBuffered(v, target)) {
      v.currentTime = target;
      this.clearPreview(); // the live video IS the feedback — don't cover it with a stale still
    } else {
      this.previewPendingFrac = frac;
      void this.pumpPreview();
    }
  }
  private isBuffered(v: HTMLVideoElement, t: number): boolean {
    for (let i = 0; i < v.buffered.length; i++) {
      if (t >= v.buffered.start(i) - 0.25 && t <= v.buffered.end(i) + 0.25) return true;
    }
    return false;
  }
  private endScrub(frac: number): void {
    this.scrubbing = false;
    this.previewGen++; // drop any in-flight preview decode
    // Released at the far-right edge → snap to LIVE (the real-time WebRTC feed).
    if (frac >= LIVE_EDGE_FRAC) {
      this.playedFrac = 1;
      this.goLive();
      this.clearPreview();
      return;
    }
    // Released in the recorded past → HLS archive playback at that point.
    const v = this.hlsVideo();
    if (v) {
      const [s, e] = this.seekableSpan(v);
      this.goRecorded(s + frac * (e - s));
    }
    this.playedFrac = frac;
    // Hold the preview until the video shows the seeked frame, then clear (no flash
    // of the pre-seek frame). A short fallback covers a 'seeked' that never fires.
    if (this.previewUrl && v) {
      const done = (): void => {
        v.removeEventListener('seeked', done);
        clearTimeout(fallback);
        this.clearPreview();
      };
      const fallback = window.setTimeout(done, 1200);
      v.addEventListener('seeked', done, { once: true });
    } else {
      this.clearPreview();
    }
  }
  private async pumpPreview(): Promise<void> {
    if (this.previewInFlight || this.previewPendingFrac === null || !this.cameraManager) return;
    const frac = this.previewPendingFrac;
    this.previewPendingFrac = null;
    const atMs = this.wallClockForFrac(frac);
    if (atMs <= 0) return;
    const gen = this.previewGen;
    this.previewInFlight = true;
    try {
      const atNs = BigInt(Math.round(atMs)) * 1_000_000n;
      // maxDimension keeps the DECODE light (the keyframe itself is one cheap source
      // read); the preview doesn't need full res to read the scene while scrubbing.
      const blob = await this.cameraManager.snapshot(this.cameraId, atNs, { maxDimension: 720 });
      if (gen === this.previewGen) this.setPreview(URL.createObjectURL(blob));
    } catch {
      /* a dropped scrub frame is fine — the next pump chases the latest position */
    } finally {
      this.previewInFlight = false;
      if (gen === this.previewGen && this.previewPendingFrac !== null) void this.pumpPreview();
    }
  }
  private setPreview(url: string): void {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = url;
  }
  private clearPreview(): void {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = undefined;
    }
  }

  // One-shot connect bridge: the latest keyframe, shown over the black <video> while
  // hls.js fetches the manifest + first segments, so the recording paints instantly.
  // Dropped (videoReady) once the <video> decodes its first real frame.
  private async fetchBridge(): Promise<void> {
    const m = this.cameraManager;
    if (!m || !this.cameraId) return;
    const gen = this.bridgeGen;
    try {
      const blob = await m.snapshot(this.cameraId, 0n, { maxDimension: 1280 });
      if (gen !== this.bridgeGen || !this.isConnected || this.videoReady) return; // stale / already painted
      const url = URL.createObjectURL(blob);
      const old = this.bridgeUrl;
      this.bridgeUrl = url;
      if (old) URL.revokeObjectURL(old);
    } catch {
      /* no keyframe yet → the loading ring covers the gap */
    }
  }
  private clearBridge(): void {
    if (this.bridgeUrl) {
      URL.revokeObjectURL(this.bridgeUrl);
      this.bridgeUrl = undefined;
    }
  }

  private fmtClock(ms: number): string {
    if (!(ms > 0)) return '—';
    return fmtTime(ms, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  render() {
    if (!this.hasFootage) {
      return html`<div class="cam-pb empty">
        <div class="cam-pb-overlay"><vitesse-icon name="cctv" size="26"></vitesse-icon><span>${t('camera.recordingEmpty')}</span></div>
      </div>`;
    }
    // Handle: pinned to the right edge while LIVE (the WebRTC edge); the played fraction
    // while reviewing; the drag fraction while scrubbing.
    const handleFrac = this.scrubbing ? this.scrubFrac : this.live ? 1 : this.playedFrac;
    const handlePct = `${handleFrac * 100}%`;
    const loc = getLocale();
    void this.nowTick; // read the freshness tick so the relative "X ago" label re-renders
    const circular = this.camera?.recordingCircular ?? true;
    // Left label = oldest recorded footage (fragment PDT). The right end is the LIVE edge,
    // labelled by the LIVE badge — not a recorded timestamp.
    const [spanLoMs] = this.mediaSpanWallClock();
    const oldestLabel = circular ? fmtAgo(spanLoMs, loc) : fmtAbs(spanLoMs, loc);
    // HLS loading/error chrome only matters while REVIEWING; at the live edge the WebRTC
    // <vitesse-camera-feed> shows its own state.
    const showBridge = !this.live && !!this.bridgeUrl && !this.videoReady && this.status !== 'error';
    // Time bubble: while scrubbing (the scrub clock) or reviewing playback (the playhead
    // clock). At the live edge the LIVE badge says it all — no bubble.
    const showBubble = this.scrubbing || (!this.live && this.headMs > 0);
    return html`<div class="cam-pb ${this.scrubbing ? 'scrubbing' : ''}">
      <!-- Dark video STAGE: the WebRTC live feed (kept MOUNTED, hidden while reviewing so
           return-to-live is instant) stacked with the HLS archive <video>. -->
      <div class="cam-pb-stage">
        <vitesse-camera-feed
          class="cam-pb-live-feed"
          variant="detail"
          ?hidden=${!this.live}
          .gatewayId=${this.gatewayId}
          .cameraId=${this.cameraId}
          .cameraStreams=${this.cameraStreams}
        ></vitesse-camera-feed>
        <video class="cam-pb-video" muted playsinline ?hidden=${this.live}></video>
        ${showBridge ? html`<img class="cam-pb-bridge" src=${this.bridgeUrl} alt="" />` : nothing}
        ${
          !this.live && this.status === 'loading' && !showBridge
            ? html`<div class="cam-pb-overlay"><div class="cam-pb-ring"></div></div>`
            : !this.live && this.status === 'error'
              ? html`<div class="cam-pb-overlay">
                  <vitesse-icon name="triangle-alert" size="24"></vitesse-icon><span>${t('camera.recordingError')}</span>
                </div>`
              : nothing
        }
      </div>
      <!-- THEMED control strip (in-flow, app surfaces — not a black overlay). -->
      ${
        this.status === 'ready'
          ? html`<div class="cam-pb-strip">
              ${
              !this.live
                ? iconButton({
                    icon: this.playing ? 'pause' : 'play',
                    title: this.playing ? t('camera.pause') : t('camera.play'),
                    onClick: () => this.togglePlay(),
                  })
                : nothing
            }
              <span class="cam-pb-edge">${oldestLabel}</span>
              <div class="cam-pb-track" @pointerdown=${this.onScrubDown}>
                ${
                this.previewUrl
                  ? html`<img
                      class="cam-pb-preview"
                      style="left:clamp(90px, ${handlePct}, calc(100% - 90px))"
                      src=${this.previewUrl}
                      alt=""
                    />`
                  : nothing
              }
                ${
                showBubble
                  ? html`<div class="cam-pb-bubble" style="left:clamp(30px, ${handlePct}, calc(100% - 30px))">
                      ${this.fmtClock(this.headMs)}
                    </div>`
                  : nothing
              }
                <div class="cam-pb-rail">
                  <div class="cam-pb-buf" style="width:${this.bufferedFrac * 100}%"></div>
                  <div class="cam-pb-played" style="width:${handlePct}"></div>
                </div>
                <div class="cam-pb-handle" style="left:${handlePct}"></div>
              </div>
              <!-- LIVE: the app's status badge — red AT the edge, gray + clickable (→ back to live) reviewing. -->
              <button class="cam-pb-live-badge" @click=${this.goLive} title=${t('camera.live')} ?data-live=${this.live}>
                ${statusBadge(this.live ? 'red' : 'gray', t('camera.live'))}
              </button>
              ${iconButton({ icon: 'maximize', title: t('camera.fullscreen'), onClick: () => this.toggleFullscreen() })}
            </div>`
          : nothing
      }
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-camera-playback': CameraPlayback;
  }
}
